// Environment Configuration for Family Trading System
const ENVIRONMENTS = {
  DEVELOPMENT: {
    name: 'Development',
    apiUrl: '',
    wsUrl: `${window.location.protocol === 'https:' ? 'wss' : 'ws'}://${window.location.host}`,
    debug: true,
    logLevel: 'debug',
    features: {
      userManagement: true,
      advancedTrading: true,
      realTimeUpdates: true,
      adminPanel: true,
      testingMode: true
    },
    database: {
      type: 'localStorage',
      prefix: 'dev_'
    },
    trading: {
      simulationMode: true,
      maxTrades: 50,
      riskLevel: 'high'
    }
  },
  PRODUCTION: {
    name: 'Production',
    apiUrl: 'https://your-vultr-domain.com/api',
    wsUrl: 'wss://your-vultr-domain.com/ws',
    debug: false,
    logLevel: 'error',
    features: {
      userManagement: true,
      advancedTrading: true,
      realTimeUpdates: true,
      adminPanel: false,
      testingMode: false
    },
    database: {
      type: 'server',
      prefix: 'prod_'
    },
    trading: {
      simulationMode: false,
      maxTrades: 10,
      riskLevel: 'medium'
    }
  }
};

class EnvironmentManager {
  constructor() {
    this.currentEnvironment = this.detectEnvironment();
    this.config = ENVIRONMENTS[this.currentEnvironment];
    this.initializeEnvironment();
  }

  detectEnvironment() {
    // Check URL for environment indicators
    const hostname = window.location.hostname;
    const port = window.location.port;
    
    // Development indicators
    if (hostname === 'localhost' || hostname === '127.0.0.1' || port === '3000') {
      return 'DEVELOPMENT';
    }
    
    // Production indicators (your Vultr domain)
    if (hostname.includes('vultr') || hostname.includes('your-domain') || hostname.includes('knoxfamilybot')) {
      return 'PRODUCTION';
    }
    
    // Default to development for safety
    return 'DEVELOPMENT';
  }

  initializeEnvironment() {
    // Set up environment-specific configurations
    this.setupLogging();
    this.setupDatabase();
    this.setupTrading();
    this.setupFeatures();
  }

  setupLogging() {
    if (this.config.debug) {
      console.log(`🚀 Running in ${this.config.name} mode`);
      console.log(`📡 API URL: ${this.config.apiUrl}`);
      console.log(`🔧 Debug mode: ${this.config.debug}`);
    }
  }

  setupDatabase() {
    const dbConfig = this.config.database;
    if (dbConfig.type === 'localStorage') {
      // Use localStorage with prefix for development
      this.database = {
        get: (key) => {
          const value = localStorage.getItem(`${dbConfig.prefix}${key}`);
          return value ? JSON.parse(value) : null;
        },
        set: (key, value) => {
          localStorage.setItem(`${dbConfig.prefix}${key}`, JSON.stringify(value));
        },
        remove: (key) => {
          localStorage.removeItem(`${dbConfig.prefix}${key}`);
        }
      };
    } else {
      // Use server database for production
      this.database = {
        get: async (key) => {
          const response = await fetch(`${this.config.apiUrl}/data/${key}`);
          return response.ok ? await response.json() : null;
        },
        set: async (key, value) => {
          await fetch(`${this.config.apiUrl}/data/${key}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(value)
          });
        },
        remove: async (key) => {
          await fetch(`${this.config.apiUrl}/data/${key}`, {
            method: 'DELETE'
          });
        }
      };
    }
  }

  setupTrading() {
    const tradingConfig = this.config.trading;
    
    // Set trading limits based on environment
    window.tradingLimits = {
      maxTrades: tradingConfig.maxTrades,
      riskLevel: tradingConfig.riskLevel,
      simulationMode: tradingConfig.simulationMode
    };

    // Override trading functions for production safety
    if (this.currentEnvironment === 'PRODUCTION') {
      this.setupProductionSafety();
    }
  }

  setupProductionSafety() {
    // Add production safety measures
    window.addEventListener('beforeunload', (e) => {
      if (window.activeTrades && window.activeTrades.length > 0) {
        e.preventDefault();
        e.returnValue = 'You have active trades. Are you sure you want to leave?';
      }
    });

    // Disable certain development features
    if (window.console && !this.config.debug) {
      console.log = () => {};
      console.warn = () => {};
      console.info = () => {};
    }
  }

  setupFeatures() {
    const features = this.config.features;
    
    // Enable/disable features based on environment
    window.features = features;
    
    // Hide admin panel in production
    if (!features.adminPanel) {
      const adminElements = document.querySelectorAll('.admin-only');
      adminElements.forEach(el => el.style.display = 'none');
    }
  }

  // Environment switching (for development)
  switchEnvironment(env) {
    if (env === 'DEVELOPMENT' && this.currentEnvironment === 'PRODUCTION') {
      console.warn('⚠️ Switching from Production to Development');
      this.currentEnvironment = env;
      this.config = ENVIRONMENTS[env];
      this.initializeEnvironment();
    } else if (env === 'PRODUCTION' && this.currentEnvironment === 'DEVELOPMENT') {
      console.warn('⚠️ Switching from Development to Production');
      this.currentEnvironment = env;
      this.config = ENVIRONMENTS[env];
      this.initializeEnvironment();
    }
  }

  // Get current environment info
  getEnvironmentInfo() {
    return {
      name: this.config.name,
      apiUrl: this.config.apiUrl,
      debug: this.config.debug,
      features: this.config.features,
      trading: this.config.trading
    };
  }

  // Check if feature is enabled
  isFeatureEnabled(feature) {
    return this.config.features[feature] || false;
  }

  // Get API URL for requests
  getApiUrl(endpoint = '') {
    return `${this.config.apiUrl}${endpoint}`;
  }

  // Get WebSocket URL
  getWebSocketUrl() {
    return this.config.wsUrl;
  }

  // Log with environment-appropriate level
  log(level, message, data = null) {
    if (this.config.debug || level === 'error') {
      console[level](`[${this.config.name}] ${message}`, data);
    }
  }
}

// Create global environment manager
const environmentManager = new EnvironmentManager();

// Export for use in components
export default environmentManager;

// Export individual functions for convenience
export const {
  getEnvironmentInfo,
  isFeatureEnabled,
  getApiUrl,
  getWebSocketUrl,
  log,
  database
} = environmentManager;

// Environment-specific constants
export const IS_DEVELOPMENT = environmentManager.currentEnvironment === 'DEVELOPMENT';
export const IS_PRODUCTION = environmentManager.currentEnvironment === 'PRODUCTION';
export const API_URL = environmentManager.getApiUrl();
export const WS_URL = environmentManager.getWebSocketUrl();